import { z } from "zod";

export const TenantSlugSchema = z.enum(["spielmann", "blackcoffee"]);
export type TenantSlug = z.infer<typeof TenantSlugSchema>;

export const TENANT_HOSTS: Record<string, TenantSlug> = {
  "admin.spielmann-consulting.com": "spielmann",
  "dataroom.spielmann-consulting.com": "spielmann",
  "dataroom-spielmann-consulting.com": "spielmann",
  "localhost:5173": "spielmann",
  "127.0.0.1:5173": "spielmann",
  "admin.blackcoffeegroup.ch": "blackcoffee",
  "dataroom.blackcoffeegroup.ch": "blackcoffee",
};

export const RoomTypeSchema = z.enum([
  "ma_sellside",
  "li_holding",
  "real_estate",
  "investor_ir",
  "franchise",
  "client_portal",
  "closing_archive",
]);
export type RoomType = z.infer<typeof RoomTypeSchema>;

export const PermissionSchema = z.enum([
  "no_access",
  "view",
  "download",
  "upload",
  "admin",
]);
export type Permission = z.infer<typeof PermissionSchema>;

export const StageGateSchema = z.enum(["teaser", "diligence", "confirmatory"]);
export type StageGate = z.infer<typeof StageGateSchema>;

export const InvestorTierSchema = z.enum(["tier_a", "tier_b", "tier_c"]);
export type InvestorTier = z.infer<typeof InvestorTierSchema>;

export const DocumentStatusSchema = z.enum([
  "uploading",
  "processing",
  "ready",
  "quarantined",
  "failed",
]);
export type DocumentStatus = z.infer<typeof DocumentStatusSchema>;

export const CreateRoomSchema = z.object({
  name: z.string().min(1).max(200),
  description: z.string().max(2000).optional(),
  templateId: z.string().optional(),
  roomType: RoomTypeSchema.optional(),
  primaryLocale: z.enum(["de", "en", "fr"]).default("de"),
});
export type CreateRoomInput = z.infer<typeof CreateRoomSchema>;

export const AccessPolicySchema = z.object({
  requireEmail: z.boolean().default(true),
  requireEmailVerify: z.boolean().default(true),
  requirePassword: z.boolean().default(false),
  requireNda: z.boolean().default(true),
  allowDownload: z.boolean().default(false),
  watermarkEnabled: z.boolean().default(true),
  expiresAt: z.string().datetime().nullable().optional(),
  allowList: z.array(z.string().email()).default([]),
  denyList: z.array(z.string().email()).default([]),
});
export type AccessPolicy = z.infer<typeof AccessPolicySchema>;

export const CreateRecipientSchema = z.object({
  email: z.string().email(),
  name: z.string().min(1).max(200).optional(),
  groupId: z.string().optional(),
  stageGate: StageGateSchema.optional(),
  sendInvite: z.boolean().default(true),
});
export type CreateRecipientInput = z.infer<typeof CreateRecipientSchema>;

export const BulkRecipientsSchema = z.object({
  recipients: z.array(
    z.object({
      email: z.string().email(),
      name: z.string().optional(),
      group: z.string().optional(),
      stage: StageGateSchema.or(InvestorTierSchema).optional(),
    }),
  ),
});
export type BulkRecipientsInput = z.infer<typeof BulkRecipientsSchema>;

export const PageEventSchema = z.object({
  sessionId: z.string().optional(),
  documentId: z.string(),
  pageNumber: z.number().int().positive(),
  dwellMs: z.number().int().nonnegative(),
  event: z.enum([
    "page_view",
    "page_leave",
    "page_dwell",
    "session_start",
    "session_end",
    "download",
  ]),
});
export type PageEvent = z.infer<typeof PageEventSchema>;

const SIGNATURE_DATA_URL_RE = /^data:image\/(png|jpe?g|webp);base64,[A-Za-z0-9+/]+=*$/;

/** Visitor NDA pad must export a real image, not an empty canvas. */
export function isValidSignatureDataUrl(value: string | null | undefined): boolean {
  if (!value || !SIGNATURE_DATA_URL_RE.test(value)) return false;
  const b64 = value.split(",")[1] ?? "";
  return b64.length >= 120;
}

export const NdaAcceptSchema = z.object({
  fullName: z.string().trim().min(1).max(200),
  signatureDataUrl: z
    .string()
    .min(1)
    .refine((v) => isValidSignatureDataUrl(v), { message: "signature_required" }),
  agreed: z.literal(true),
});
export type NdaAcceptInput = z.infer<typeof NdaAcceptSchema>;

export const ViewerVerifyEmailSchema = z.object({
  email: z.string().email(),
  name: z.string().max(200).optional(),
  turnstileToken: z.string().optional(),
});

export const ViewerConfirmEmailSchema = z.object({
  code: z.string().min(8).max(128),
});

export const ViewerVerifyPasswordSchema = z.object({
  password: z.string().min(1),
});

export const UpdateAccessLinkSchema = z.object({
  requireNda: z.boolean().optional(),
  allowDownload: z.boolean().optional(),
  watermarkEnabled: z.boolean().optional(),
  expiresAt: z.string().datetime().nullable().optional(),
  password: z.string().min(1).max(200).nullable().optional(),
});
export type UpdateAccessLinkInput = z.infer<typeof UpdateAccessLinkSchema>;

export const UploadUrlSchema = z.object({
  roomId: z.string(),
  folderId: z.string().nullable().optional(),
  filename: z.string().min(1).max(500),
  mimeType: z.string(),
  sizeBytes: z.number().int().positive().max(2 * 1024 * 1024 * 1024),
});

export const INTEREST_SCORE_WEIGHTS = {
  activeDwell: 0.25,
  completion: 0.2,
  highIntentPages: 0.2,
  returnVisits: 0.15,
  recency: 0.1,
  forwardDetection: 0.05,
  download: 0.05,
} as const;

export function interestLabel(score: number): string {
  if (score >= 80) return "Highly engaged";
  if (score >= 60) return "Active diligence";
  if (score >= 40) return "Exploring";
  if (score >= 20) return "Low interest";
  return "Cold / ignored";
}

export const PERFORMANCE_BUDGETS = {
  lcpViewerShellMs: 2000,
  firstPageVisibleP95Ms: 2500,
  tileCachedP95Ms: 200,
  tileColdP95Ms: 500,
  folderTree500TtiMs: 1000,
} as const;

export const SLO_TARGETS = {
  firstPageP95Ms: 2500,
  tileFetchP99Ms: 400,
  ingestCompletionP95Ms: 180_000,
  magicLinkSuccessRate: 0.995,
  revokePropagationMs: 30_000,
} as const;

export { buildWatermarkText, type WatermarkInput } from "./watermark";
